import { MenuItem } from 'primeng/api';

/** A visible node of the sidebar tree. */
export interface MenuNode {
  /** Path of labels from the root: stable across permission changes, unlike array indexes. */
  key: string;
  label: string;
  icon?: string;
  routerLink?: any[];
  /** Root-level heading (e.g. STAFFING). */
  section: boolean;
  children: MenuNode[];
}

/**
 * Turns the permission-filtered menu model into the rendered tree: hidden items are dropped,
 * and so is any heading or group left without a destination (no route and no visible child).
 */
export function buildMenuTree(items: MenuItem[] | undefined, parentKey = ''): MenuNode[] {
  const nodes: MenuNode[] = [];
  for (const item of items ?? []) {
    if (item.visible === false || item.separator || !item.label) continue;

    const key = parentKey ? `${parentKey}/${item.label}` : item.label;
    const children = buildMenuTree(item.items, key);
    const routerLink = Array.isArray(item.routerLink) ? item.routerLink : undefined;
    if (!routerLink && children.length === 0) continue;

    nodes.push({ key, label: item.label, icon: item.icon, routerLink, section: !parentKey, children });
  }
  return nodes;
}

/** Keys of every node that has an active route below it, i.e. the branches to open and highlight. */
export function findActiveTrail(nodes: MenuNode[], isActive: (routerLink: any[]) => boolean): Set<string> {
  const trail = new Set<string>();
  const visit = (node: MenuNode): boolean => {
    let hasActiveChild = false;
    for (const child of node.children) {
      // Visit every child (no short-circuit) so sibling branches are recorded too.
      hasActiveChild = visit(child) || hasActiveChild;
    }
    if (hasActiveChild) trail.add(node.key);
    return hasActiveChild || (!!node.routerLink && isActive(node.routerLink));
  };
  nodes.forEach(visit);
  return trail;
}

/** Keys of every node that can be expanded. */
export function collectBranchKeys(nodes: MenuNode[]): string[] {
  return nodes.flatMap((node) => (node.children.length ? [node.key, ...collectBranchKeys(node.children)] : []));
}

/** Nodes from the root down to the deepest node whose route is active (empty when none is). */
export function findActivePath(nodes: MenuNode[], isActive: (routerLink: any[]) => boolean): MenuNode[] {
  let deepest: MenuNode[] = [];
  const visit = (node: MenuNode, ancestors: MenuNode[]) => {
    const path = [...ancestors, node];
    if (path.length > deepest.length && node.routerLink && isActive(node.routerLink)) deepest = path;
    node.children.forEach((child) => visit(child, path));
  };
  nodes.forEach((node) => visit(node, []));
  return deepest;
}

/** Where a node leads: its own page, else the first page below it (sections and groups have none of their own). */
export function nodeLink(node: MenuNode): any[] | undefined {
  if (node.routerLink) return node.routerLink;
  for (const child of node.children) {
    const link = nodeLink(child);
    if (link) return link;
  }
  return undefined;
}
